import {
  type DatePickerProps as BaseDatePickerProps,
  DatePicker as BaseDatePicker,
} from '@mantine/dates';
import { useMergedRef } from '@mantine/hooks';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import { alpha, styled } from '@mui/material/styles';
import dayjs from 'dayjs';
import { forwardRef, useEffect, useRef, useState } from 'react';

import 'dayjs/locale/th';
import { CalendarTodayIcon, CloseRoundedIcon } from '../../icons';
import { ColorPalette } from '../../theme';

type DateInputProps = Omit<BaseDatePickerProps, 'sx'> & {
  helperText?: string;
  defaultTime?: { hour?: number; minute?: number; second?: number };
};

const StyledInputLabel = styled(InputLabel)({
  position: 'absolute',
  top: 0,
  left: 0,
});

const StyledDateInput = styled(BaseDatePicker, {
  shouldForwardProp: (prop) => prop !== 'color' && prop !== 'error',
})<Omit<BaseDatePickerProps, 'sx'> & { color?: ColorPalette; error?: boolean }>(({
  theme,
  color: colorProp,
  error,
}) => {
  const color = (error ? ColorPalette.ERROR : colorProp) ?? ColorPalette.PRIMARY;
  return {
    marginTop: theme.spacing(6),
    border: '1px solid',
    borderColor: error ? theme.palette.error.main : theme.palette.grey['300'],
    borderRadius: theme.shape.rounded(1),
    '& .mantine-DatePicker-day[data-selected="true"]': {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.text.primary,
      fontWeight: 600, //  TODO: use theme.typography.fontWeightSemibold
    },
    '& .mantine-DatePicker-monthPickerControlActive, & .mantine-DatePicker-yearPickerControlActive':
      {
        backgroundColor: theme.palette.primary.main,
      },
    input: {
      fontFamily: theme.typography.fontFamily,
      color: theme.palette.text.primary,
      fontSize: theme.typography.fontSize,
      lineHeight: 1.5,
      fontWeight: theme.typography.fontWeightRegular,
      border: 'none',
      boxSizing: 'border-box',
      minHeight: '42px',
      padding: '16.5px 14px',
      height: '1.4375em',

      ':hover': { borderColor: theme.palette[color].main },
      ':focus': {
        borderColor: theme.palette[color].main,
        boxShadow: `${alpha(theme.palette[color].main, 0.25)} 0 0 0 3px`,
      },
      ':disabled': {
        opacity: 1,
        cursor: 'default',
        color: theme.palette.text.disabled,
        backgroundColor: '#F1F3F5',
        ':hover': { borderColor: theme.palette.grey['300'] },
        '::placeholder': { color: theme.palette.text.disabled },
      },
    },
  };
});

const DateInput = forwardRef((props: DateInputProps, forwardedRef) => {
  const {
    label,
    value,
    onChange,
    helperText,
    error,
    color,
    clearable: isClearable = true,
    required: isRequired,
    fullWidth: isFullWidth = true,
    defaultTime,
    ...restProps
  } = props;

  const DateInputRef = useRef<HTMLInputElement>(null);
  const ref = useMergedRef(DateInputRef, forwardedRef);
  const [date, setDate] = useState<Date | null>(value ? new Date(value) : null);

  const {
    hour: hourProp = 23,
    minute: minuteProp = 59,
    second: secondProp = 59,
  } = defaultTime ?? {};

  useEffect(() => {
    setDate(value ? new Date(value) : null);
  }, [value]);

  const handleChange = (value: Date | null) => {
    const hour = dayjs(date).get('hour');
    const minute = dayjs(date).get('minute');

    const dateWithChange = date
      ? dayjs(value).set('hour', hour).set('minute', minute).toDate()
      : dayjs(value)
          .set('hour', hourProp)
          .set('minute', minuteProp)
          .set('second', secondProp)
          .toDate();

    setDate(dateWithChange);
    onChange?.(dateWithChange);
  };

  const handleClear = () => {
    setDate(null);
    onChange?.(null);
  };

  const rightButton =
    !isClearable || !date ? (
      <CalendarTodayIcon sx={{ color: 'text.disabled', pointerEvents: 'none', fontSize: 24 }} />
    ) : (
      <IconButton onClick={handleClear} disabled={props.disabled} sx={{ color: 'text.secondary' }}>
        <CloseRoundedIcon />
      </IconButton>
    );

  return (
    <FormControl fullWidth={isFullWidth} required={isRequired}>
      {label ? <StyledInputLabel>{label}</StyledInputLabel> : null}
      <StyledDateInput
        value={date}
        ref={ref}
        locale='th'
        labelFormat='MMMM YYYY'
        inputFormat='YYYY-MM-DD'
        yearLabelFormat='YYYY'
        size='md'
        firstDayOfWeek='sunday'
        color={color as ColorPalette}
        rightSection={rightButton}
        rightSectionProps={{ style: { width: 0, right: 24 } }}
        onChange={handleChange}
        error={!!error}
        dateParser={(dateString) => new Date(Date.parse(dateString))}
        {...restProps}
      />

      <FormHelperText error={!!error}>{helperText}</FormHelperText>
    </FormControl>
  );
});

DateInput.displayName = 'DateInput';

export { DateInput };
export type { DateInputProps };
